import { navigate } from '@reach/router'
import React, { useState, useEffect, useContext } from 'react'
import { useLocalStorage, useLocation } from 'react-use'
import { ShopifyContext } from '../components/Layout'
import { config } from '../config'
import { getActualShopifyId, getEncodedShopifyId } from './useShopifyFunctions'

const parseParams = (params, keyName) =>
  [...params.entries()]
    .filter(([key]) => key.startsWith(keyName))
    .reduce((acc, [key, value]) => {
      return {
        ...acc,
        [key]: value,
      }
    }, {})

const persistedStateId = 'loopCartInfo'
const shopUrl = sub => `https://ordersupport.${sub}.com/#/cart/v2/`
const loopApiUrl = 'https://api.loopreturns.com/api/v1/cart/'
const LoopCartClient = {
  deleteCart: async token => {
    return await fetch(`${loopApiUrl}${token}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'x-authorization': config.GATSBY_LOOP_CART_KEY,
      },
    })
      .then(res => res.json())
      .catch(e => {
        console.log(e)
        return e
      })
  },
  fetchCart: async token => {
    return await fetch(`${loopApiUrl}${token}`, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        'x-authorization': config.GATSBY_LOOP_CART_KEY,
      },
    })
      .then(res => res.json())
      .catch(e => {
        console.log(e)
        return e
      })
  },
  updateCart: async (token, variantIds) => {
    return await fetch(`${loopApiUrl}${token}`, {
      method: 'POST',
      body: JSON.stringify({
        cart: variantIds,
      }),
      headers: {
        'content-type': 'application/json',
        'x-authorization': config.GATSBY_LOOP_CART_KEY,
      },
    })
      .then(res => {
        return res.json()
      })
      .catch(e => {
        console.log(e)
        return e
      })
  },
  createCart: async variantIds => {
    return await fetch(loopApiUrl, {
      method: 'POST',
      body: JSON.stringify({
        cart: variantIds,
      }),
      headers: {
        'content-type': 'application/json',
        'x-authorization': config.GATSBY_LOOP_CART_KEY,
      },
    })
      .then(res => {
        return res.json()
      })
      .catch(e => {
        console.log(e)
        return e
      })
  },
}

function useLoop() {
  const { clearCart, setCartHasItems, addItem, checkout, ...rest } = useContext(
    ShopifyContext
  )
  const [loopCartInfoLS, setLoopCartInfoLS] = useLocalStorage(
    persistedStateId,
    ''
  )

  const location = useLocation()
  const checkoutLoaded = checkout && checkout.loaded

  useEffect(() => {
    async function handleReturningCart(currCartId, params) {
      const pastLoopCart = await LoopCartClient.fetchCart(currCartId)

      if (pastLoopCart && pastLoopCart.cart) {
        await clearCart()
        await Promise.all(
          pastLoopCart.cart.map(async element => {
            return await addItem({
              variantId: getEncodedShopifyId(element, 'ProductVariant'),
              quantity: 1,
              onFinish: setCartHasItems,
            })
          })
        )
        const paramsInfo = parseParams(params, 'loop')

        setLoopCartInfoLS(paramsInfo)
        navigate(location.pathname, { replace: true })
      }
    }

    if (checkoutLoaded) {
      let paramsObject = null

      paramsObject = loopCartInfoLS ? loopCartInfoLS : null

      if (location.search.includes('loop_total')) {
        const params = new URLSearchParams(window.location.search)

        //handle return users

        const isReturningLoop =
          loopCartInfoLS.cartToken &&
          loopCartInfoLS.loop_return_id === params.get('loop_return_id')

        if (isReturningLoop) {
          handleReturningCart(loopCartInfoLS.cartToken, params)
        } else {
          // Remove any non-loop related query params, then convert the query string
          // to an object of key/value pairs to make it easier to work with
          paramsObject = parseParams(params, 'loop')
          setLoopCartInfoLS(paramsObject)
          clearCart()

          navigate(location.pathname, { replace: true })
        }
      }
    }
  }, [location, checkoutLoaded])

  async function createOrUpdateLoopCart(variantIds, onFinish) {
    const cartInfo = loopCartInfoLS

    const shopUrlCreate = shopUrl(cartInfo.loop_subdomain)
    let res = { token: cartInfo.cartToken }
    if (cartInfo.cartToken && cartInfo.loop_return_id) {
      res = await LoopCartClient.updateCart(
        loopCartInfoLS.cartToken,
        variantIds.map(id => getActualShopifyId(id, 'ProductVariant'))
      )
    } else {
      res = await LoopCartClient.createCart(
        variantIds.map(id => getActualShopifyId(id, 'ProductVariant'))
      )
    }

    await setLoopCartInfoLS({
      loop_return_id: loopCartInfoLS.loop_return_id,
      cartToken: res.token,
    })

    onFinish()
    console.log(`Navigating to: ${shopUrlCreate}${res.token}`)
    navigate(`${shopUrlCreate}${res.token}`)
  }

  return {
    isLoopCart: loopCartInfoLS && loopCartInfoLS.loop_total ? true : false,
    createOrUpdateLoopCart,
    ...loopCartInfoLS,
  }
}

export default useLoop
